<template>
  <div class="vm-permission-guide">
    <div class="vm-permission-guide__header">
      <div class="vm-permission-guide__icon" aria-hidden="true">
        <i class="mdi mdi-camera-off-outline"></i>
      </div>
      <h3 class="vm-permission-guide__title">Enable camera &amp; microphone</h3>
      <p class="vm-permission-guide__lead">
        To record your visume, your browser needs permission to use your camera and microphone.
      </p>
    </div>

    <section class="vm-permission-guide__section" aria-labelledby="vm-perm-primary-heading">
      <h4 id="vm-perm-primary-heading" class="vm-permission-guide__section-title">
        <i class="mdi" :class="platformIcon" aria-hidden="true"></i>
        {{ platformLabel }}
      </h4>
      <ol class="vm-permission-guide__steps">
        <li v-for="(step, index) in primarySteps" :key="'primary-' + index" v-html="step"></li>
      </ol>
    </section>

    <section
      v-if="secondarySteps.length"
      class="vm-permission-guide__section vm-permission-guide__section--muted"
      aria-labelledby="vm-perm-secondary-heading"
    >
      <h4 id="vm-perm-secondary-heading" class="vm-permission-guide__section-title">
        <i class="mdi mdi-shield-lock-outline" aria-hidden="true"></i>
        If you previously blocked access
      </h4>
      <ol class="vm-permission-guide__steps">
        <li v-for="(step, index) in secondarySteps" :key="'secondary-' + index" v-html="step"></li>
      </ol>
    </section>

    <section class="vm-permission-guide__section vm-permission-guide__section--tips" aria-labelledby="vm-perm-tips-heading">
      <h4 id="vm-perm-tips-heading" class="vm-permission-guide__section-title">
        <i class="mdi mdi-lightbulb-on-outline" aria-hidden="true"></i>
        Still not working?
      </h4>
      <ul class="vm-permission-guide__tips">
        <li v-for="(tip, index) in troubleshootingTips" :key="'tip-' + index">{{ tip }}</li>
      </ul>
    </section>

    <div class="vm-permission-guide__actions">
      <button type="button" class="vm-modal__btn vm-modal__btn--outline" @click="$emit('close')">
        Got it
      </button>
      <button type="button" class="vm-modal__btn vm-modal__btn--primary" @click="$emit('retry')">
        Try again
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CameraPermissionGuide',
  computed: {
    isMobile() {
      return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    },
    isIOS() {
      return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    },
    isAndroid() {
      return /Android/i.test(navigator.userAgent);
    },
    platformLabel() {
      if (this.isIOS) return 'On iPhone or iPad';
      if (this.isAndroid) return 'On Android';
      if (this.isMobile) return 'On mobile';
      return 'On desktop';
    },
    platformIcon() {
      if (this.isIOS) return 'mdi-apple';
      if (this.isAndroid) return 'mdi-android';
      if (this.isMobile) return 'mdi-cellphone';
      return 'mdi-monitor';
    },
    primarySteps() {
      if (this.isIOS) {
        return [
          'When Safari asks, tap <strong>Allow</strong> for camera and microphone.',
          'If no prompt appears, tap <strong>aA</strong> in the address bar → <strong>Website Settings</strong> → set Camera and Microphone to <strong>Allow</strong>.',
          'Reload this page, then tap <strong>Start recording</strong> again.',
        ];
      }
      if (this.isAndroid) {
        return [
          'When Chrome asks, tap <strong>Allow</strong> for camera and microphone.',
          'If blocked earlier, tap the <strong>lock icon</strong> next to the address bar → <strong>Permissions</strong> → allow Camera and Microphone.',
          'Reload this page, then tap <strong>Start recording</strong> again.',
        ];
      }
      if (this.isMobile) {
        return [
          'When your browser asks, tap <strong>Allow</strong> for camera and microphone.',
          'Open your browser site settings and allow camera and microphone for this website.',
          'Reload this page, then try recording again.',
        ];
      }
      return [
        'When your browser prompts you, click <strong>Allow</strong> for camera and microphone.',
        'Look for a camera or lock icon to the left of the address bar — open <strong>Site settings</strong> or <strong>Permissions</strong> and allow both.',
        'Reload this page, then click <strong>Start recording</strong> again.',
      ];
    },
    secondarySteps() {
      if (this.isIOS) {
        return [
          'Go to <strong>Settings → Safari → Camera / Microphone</strong> and ensure access is allowed.',
          'In Safari, tap <strong>aA → Website Settings</strong> and set both to <strong>Allow</strong> for hireclap.com.',
        ];
      }
      if (this.isAndroid) {
        return [
          'Tap the <strong>lock icon</strong> in Chrome → <strong>Permissions</strong> → reset Camera and Microphone to <strong>Allow</strong>.',
          'Or open <strong>Chrome → Settings → Site settings → Camera / Microphone</strong> and allow hireclap.com.',
        ];
      }
      if (this.isMobile) {
        return [
          'Open your browser menu → <strong>Settings → Site permissions</strong> and allow camera and microphone for this site.',
        ];
      }
      return [
        '<strong>Chrome / Edge:</strong> Click the lock icon → <strong>Site settings</strong> → set Camera and Microphone to <strong>Allow</strong>.',
        '<strong>Firefox:</strong> Click the shield or lock icon → <strong>Permissions</strong> → allow Camera and Microphone.',
        '<strong>Safari (Mac):</strong> Safari menu → <strong>Settings for This Website</strong> → allow Camera and Microphone.',
      ];
    },
    troubleshootingTips() {
      const tips = [
        'Use an up-to-date browser: Chrome, Safari, Firefox, or Edge.',
        'Close other apps that may be using your camera (Zoom, Teams, Meet, etc.).',
        'Make sure you are on a secure connection (https://).',
      ];
      if (this.isIOS) {
        tips.push('On iOS, recording works best in Safari.');
      }
      if (this.isAndroid) {
        tips.push('On Android, Chrome usually gives the most reliable results.');
      }
      tips.push('After changing permissions, reload the page before recording again.');
      return tips;
    },
  },
};
</script>
